import api from '@/lib/api'

const DefaultTypes = [
  {key: 'type1', label: 'Action', color: 'color1'},
  {key: 'type2', label: 'Alert', color: 'color2'},
  {key: 'type3', label: 'Trend', color: 'color3'}
]

const DefaultStatuses = [
  {key: 'status1', label: 'Evaluation'},
  {key: 'status2', label: 'Backlog'},
  {key: 'status3', label: 'WIP'},
  {key: 'status4', label: 'Finished'},
  {key: 'status5', label: 'Discarded'}
]

const DefaultMatrixLabels = {
  Axis1: {
    min: 'Tactic',
    max: 'Strategic'
  },
  Axis2: {
    min: 'Normal',
    max: 'Urgent'
  },
  Axis3: {
    min: 'Simple',
    max: 'Complex',
    legend: 'complexity'
  }
}

const DefaultTemplate = `<h2>Executive summary</h2>[Write a few lines that describe the insight. Be concise but thorough.]
<br>
<h2>Action Plan</h2>[Describe which are the next actionable steps to follow in order to implement the insight.]
<br>
<h2>Premises</h2>[Describe the issues to be resolved to fully evaluate the potential of the insight.]
<br>
<h2>Economic impact</h2>[Briefly describe the foreseen economic impact of the insight, if implemented.]
<br>`

const state = {
  template: null,
  matrixLabels: null,
  types: null,
  maxNumberTypes: 5,
  maxNumberStatuses: 9,
  statuses: null,
  loaded: false
}

const getters = {
  loaded(state) {
    return state.loaded
  },
  template(state) {
    return state.template || DefaultTemplate
  },
  isDefaultTemplate(state) {
    return !state.template || state.template === DefaultTemplate
  },
  isDefaultMatrixLabels(state) {
    if (!state.matrixLabels) return true
    let defo = true
    const axisKeys = Object.keys(state.matrixLabels)
    const axisKeysLength = axisKeys.length
    for (let i = 0; defo && i < axisKeysLength; i++) {
      const axis = state.matrixLabels[axisKeys[i]]
      if (axis.min) defo = axis.min === DefaultMatrixLabels[axisKeys[i]].min
      if (axis.max) defo = axis.min === DefaultMatrixLabels[axisKeys[i]].max
      if (axis.legend)
        defo = axis.legend === DefaultMatrixLabels[axisKeys[i]].legend
    }
    return defo
  },
  matrixLabels(state) {
    return state.matrixLabels || DefaultMatrixLabels
  },
  isDefaultTypes(state) {
    if (!state.types) return true
    // TODO: backwards comp
  },
  types(state) {
    if (!Array.isArray(state.types)) return DefaultTypes
    return state.types
  },
  maxNumberTypes(state) {
    return state.maxNumberTypes
  },
  defaultType(state, getters) {
    return getters.types[0]
  },
  unassignedType() {
    return '——'
  },
  statuses(state) {
    if (!state.statuses) return DefaultStatuses
    return state.statuses
  },
  defaultStatusesLabels() {
    return DefaultStatuses.map(s => s.label)
  },
  defaultTypesLabels() {
    return DefaultTypes.map(t => t.label)
  },
  isDefaultStatuses(state) {
    if (!state.statuses) return true
    // TODO: backwards comp
  },
  maxNumberStatuses(state) {
    return state.maxNumberStatuses
  },
  getInsightType: (state, getters) => i => {
    const types = getters.types
    return types.find(t => t.key === i.type)
  },
  getInsightStatusLabel: (state, getters) => key => {
    const statuses = getters.statuses
    const status = statuses.find(s => s.key === key)
    return (status || statuses[0]).label
  }
}

const mutations = {
  template(state, template) {
    state.template = template
  },
  matrixLabels(state, data) {
    state.matrixLabels = data
  },
  statuses(state, statuses) {
    state.statuses = statuses
  },
  types(state, data) {
    state.types = data
  },
  reset(state) {
    state.template = null
    state.matrixLabels = null
    state.types = null
    state.statuses = null
    state.loaded = false
  },
  loaded(state) {
    state.loaded = true
  }
}

const actions = {
  fetchTemplate({state, dispatch}, landscapeId) {
    if (state.template) return state.template
    else
      return dispatch('fetchSettings', landscapeId).then(
        ({template}) => template
      )
  },
  fetchMatrixLabels({state, dispatch}, landscapeId) {
    if (state.matrixLabels) return state.matrixLabels
    else
      return dispatch('fetchSettings', landscapeId).then(
        ({matrixLabels}) => matrixLabels
      )
  },
  fetchSettings({commit, getters, dispatch}, landscapeId) {
    if (state.loaded) {
      return {
        template: getters.template,
        matrixLabels: getters.matrixLabels,
        types: getters.types,
        statuses: getters.statuses
      }
    }
    return api
      .call('get', `/landscapes/${landscapeId}/insights/settings`)
      .then(({template, matrixLabels, types, statuses}) => {
        commit('loaded') // TODO: set dirty?
        commit('template', template)
        commit('matrixLabels', matrixLabels)
        commit('types', types)
        commit('statuses', statuses)
        dispatch('insights/filters/filterSettings', null, {root: true})
        return {
          template: getters.template,
          matrixLabels: getters.matrixLabels,
          types: getters.types,
          statuses: getters.statuses
        }
      })
  },
  saveStatuses({commit, getters, dispatch}, {landscapeId, statuses}) {
    return api
      .call('put', `/landscapes/${landscapeId}/insights/settings/statuses`, {
        statuses
      })
      .then(data => {
        commit('statuses', data)
        dispatch('insights/filters/filterSettings', null, {root: true})
        return getters.statuses
      })
  },
  saveTemplate({commit, getters}, {landscapeId, template}) {
    return api
      .call('put', `/landscapes/${landscapeId}/insights/settings/template`, {
        template
      })
      .then(template => {
        commit('template', template)
        return getters.template
      })
  },
  saveMatrixLabels({commit, getters}, {landscapeId, matrixLabels}) {
    return api
      .call('put', `/landscapes/${landscapeId}/insights/settings/matrix`, {
        matrixLabels
      })
      .then(data => {
        commit('matrixLabels', data)
        return getters.matrixLabels
      })
  },
  saveTypes({commit, getters, dispatch}, {landscapeId, types}) {
    return api
      .call('put', `/landscapes/${landscapeId}/insights/settings/types`, {
        types
      })
      .then(data => {
        commit('types', data)
        dispatch('insights/filters/filterSettings', null, {root: true})
        return getters.types
      })
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
